/**
 * useDeviceTerminal — the browser half of the device Web CLI session.
 *
 * Flow (docs/design/device-web-terminal.md):
 *   1. POST /api/v2/devices/:serial/terminal  -> opaque session_id (owgw ids never reach JS).
 *      This can take up to ~60s: owgw waits for the device to acknowledge the rtty request.
 *   2. new WebSocket(same-origin ws_path); FIRST frame {"type":"auth","session_id"} (the id is
 *      never put in a URL, so it never lands in proxy access logs).
 *   3. Server frames: TEXT {"type":"status","state":"waiting_device"|"ready"},
 *      TEXT {"type":"closed","reason","message"}, BINARY terminal output.
 *      Client frames: BINARY keystrokes, TEXT {"type":"resize","cols","rows"} / {"type":"close"}.
 *
 * The hook owns no DOM: the component wires xterm's onData/onResize into sendInput/resize and
 * writes onOutput bytes into the terminal. Every attempt gets a sequence number so a late event
 * from a previous (disconnected) socket can never overwrite the state of the current one.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "@/lib/api/client";

export type TerminalPhase =
  | "idle"
  | "requesting"
  | "connecting"
  | "waiting_device"
  | "ready"
  | "closed"
  | "failed";

export interface OpenTerminalResponse {
  session_id: string;
  serial: string;
  ws_path: string;
  ticket_ttl_s: number;
  idle_timeout_s: number;
  max_session_s: number;
}

export interface TerminalLimits {
  idleTimeoutS: number;
  maxSessionS: number;
}

export interface TerminalStatus {
  phase: TerminalPhase;
  /** Stable machine code from the server (device_offline, idle_timeout, ...). */
  code?: string;
  /** Operator-facing text, shown verbatim. */
  message?: string;
  /** Epoch ms the shell became usable (drives the session clock). */
  readyAt?: number;
  limits?: TerminalLimits;
}

interface Handlers {
  onOutput: (bytes: Uint8Array) => void;
  onReady?: () => void;
}

/** Close reasons that mean "no shell was possible" (vs a session that ended normally). */
const FAILURE_REASONS = new Set([
  "rtty_connect_failed",
  "rtty_closed_before_ready",
  "device_attach_timeout",
  "device_login_failed",
  "invalid_session",
  "auth_timeout",
  "capacity",
  "audit_unavailable",
  "bridge_error",
]);

function failureFromOpenError(err: unknown): TerminalStatus {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as { code?: unknown };
    if (err.isTransport) {
      return { phase: "failed", code: "controller_unreachable", message: "The controller is unreachable." };
    }
    return {
      phase: "failed",
      code: typeof body.code === "string" ? body.code : `http_${err.status}`,
      message: err.message,
    };
  }
  return { phase: "failed", code: "unknown", message: String(err) };
}

export function useDeviceTerminal(serial: string, handlers: Handlers) {
  const [status, setStatus] = useState<TerminalStatus>({ phase: "idle" });
  const wsRef = useRef<WebSocket | null>(null);
  const readyRef = useRef(false);
  const attemptRef = useRef(0);
  const sizeRef = useRef<{ cols: number; rows: number } | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const encoderRef = useRef(new TextEncoder());

  /** Close the current socket politely (tell the server first so it audits client_closed). */
  const teardown = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    readyRef.current = false;
    if (!ws) return;
    try {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "close" }));
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close(1000);
    } catch {
      /* already gone */
    }
  }, []);

  const connect = useCallback(async () => {
    teardown();
    const attempt = ++attemptRef.current;
    setStatus({ phase: "requesting" });

    let opened: OpenTerminalResponse;
    try {
      opened = await api.post<OpenTerminalResponse>(`/devices/${encodeURIComponent(serial)}/terminal`);
    } catch (err) {
      if (attempt === attemptRef.current) setStatus(failureFromOpenError(err));
      return;
    }
    if (attempt !== attemptRef.current) return; // closed while owgw was answering

    const limits: TerminalLimits = {
      idleTimeoutS: opened.idle_timeout_s,
      maxSessionS: opened.max_session_s,
    };
    const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${scheme}//${window.location.host}${opened.ws_path}`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    setStatus({ phase: "connecting", limits });

    let serverClose: { reason: string; message: string } | null = null;
    let accepted = false;

    ws.onopen = () => {
      if (attempt !== attemptRef.current) return;
      ws.send(JSON.stringify({ type: "auth", session_id: opened.session_id }));
      // Queued behind auth; the server applies it when the device logs in.
      if (sizeRef.current) ws.send(JSON.stringify({ type: "resize", ...sizeRef.current }));
    };

    ws.onmessage = (ev: MessageEvent) => {
      if (attempt !== attemptRef.current) return;
      if (typeof ev.data !== "string") {
        handlersRef.current.onOutput(new Uint8Array(ev.data as ArrayBuffer));
        return;
      }
      let doc: { type?: string; state?: string; reason?: string; message?: string };
      try {
        doc = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (doc.type === "status" && doc.state === "waiting_device") {
        accepted = true;
        setStatus({ phase: "waiting_device", limits });
      } else if (doc.type === "status" && doc.state === "ready") {
        accepted = true;
        readyRef.current = true;
        setStatus({ phase: "ready", limits, readyAt: Date.now() });
        handlersRef.current.onReady?.();
      } else if (doc.type === "closed") {
        serverClose = { reason: doc.reason ?? "closed", message: doc.message ?? "Session closed." };
      }
    };

    ws.onclose = () => {
      if (attempt !== attemptRef.current) return;
      wsRef.current = null;
      readyRef.current = false;
      const closeInfo = serverClose;
      if (closeInfo) {
        setStatus({
          phase: FAILURE_REASONS.has(closeInfo.reason) ? "failed" : "closed",
          code: closeInfo.reason,
          message: closeInfo.message,
          limits,
        });
      } else if (!accepted) {
        // Refused at the handshake (role, tenancy, origin) or blocked by a proxy.
        setStatus({
          phase: "failed",
          code: "handshake_refused",
          message:
            "The controller refused the terminal connection. Your login may have expired, or a proxy is blocking websockets.",
          limits,
        });
      } else {
        setStatus({ phase: "closed", code: "connection_lost", message: "Connection to the controller was lost.", limits });
      }
    };
  }, [serial, teardown]);

  const disconnect = useCallback(() => {
    attemptRef.current++; // ignore any event still in flight from this socket
    const hadSession = wsRef.current !== null;
    teardown();
    setStatus((s) =>
      hadSession || s.phase === "requesting"
        ? { phase: "closed", code: "client_closed", message: "Session closed.", limits: s.limits }
        : s,
    );
  }, [teardown]);

  /** Keystrokes from xterm (UTF-8 encoded). Dropped until the device has logged in. */
  const sendInput = useCallback((data: string) => {
    const ws = wsRef.current;
    if (!ws || !readyRef.current || ws.readyState !== WebSocket.OPEN) return;
    ws.send(encoderRef.current.encode(data));
  }, []);

  /** Terminal geometry; remembered so a (re)connect starts at the right size. */
  const resize = useCallback((cols: number, rows: number) => {
    sizeRef.current = { cols, rows };
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "resize", cols, rows }));
  }, []);

  // Never leave a shell open behind an unmounted component.
  useEffect(
    () => () => {
      attemptRef.current++;
      teardown();
    },
    [teardown],
  );

  return { status, connect, disconnect, sendInput, resize };
}
